import { useCallback, useEffect, useMemo, useState } from 'react';

import { searchLinkablePages } from '@/lib/blocks/page-link-search';
import { canUseLocalDatabase, isOnline } from '@/lib/connectivity';
import { getSyncEngine } from '@/lib/sync/engine';

import { searchBlocks, type SearchResult } from './api.web';
import { groupByContainer } from './group-by-container';
import { searchBlocksLocal } from './local';
import type { SearchContainerResult } from './types';

async function tryLocalSearch(
  q: string,
  options?: UseContentSearchOptions,
): Promise<SearchResult[] | null> {
  if (!canUseLocalDatabase()) {
    return null;
  }
  const db = getSyncEngine().getDatabase();
  if (!db) {
    return null;
  }
  const { ensureSearchIndexMigrated } = await import('../db/search-index');
  await ensureSearchIndexMigrated(db);
  return searchBlocksLocal(db, {
    query: q,
    limit: options?.limit ?? 30,
    scopeRootIds: options?.scopeRootIds,
  });
}

async function fetchSearchResults(
  q: string,
  options?: UseContentSearchOptions,
): Promise<SearchResult[]> {
  if (!isOnline()) {
    return (await tryLocalSearch(q, options)) ?? [];
  }

  try {
    const rows = await searchBlocks({
      query: q,
      limit: options?.limit ?? 30,
      scopeRootIds: options?.scopeRootIds,
    });
    if (rows.length > 0) {
      return rows;
    }
  } catch {
    // Fall through to local FTS when hybrid API fails.
  }

  const local = await tryLocalSearch(q, options);
  if (local && local.length > 0) {
    return local;
  }

  const fallback = await searchLinkablePages(q);
  return fallback.map((page) => ({
    blockId: page.id,
    title: page.title,
    type: page.type,
    parentId: null,
    containerId: page.id,
    containerType: page.type,
    containerTitle: page.title,
    snippet: page.breadcrumb ?? '',
    score: 0,
    matchedVia: ['trigram'] as const,
  }));
}

const DEBOUNCE_MS = 300;

export type UseContentSearchOptions = {
  scopeRootIds?: string[];
  limit?: number;
  debounceMs?: number;
};

export function useContentSearch(query: string, options?: UseContentSearchOptions) {
  const [debouncedQuery, setDebouncedQuery] = useState(query.trim());
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const debounceMs = options?.debounceMs ?? DEBOUNCE_MS;

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query.trim()), debounceMs);
    return () => clearTimeout(timer);
  }, [query, debounceMs]);

  const runSearch = useCallback(
    async (q: string) => {
      if (!q) {
        setResults([]);
        setError(null);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const rows = await fetchSearchResults(q, options);
        setResults(rows);
        setError(null);
      } catch (err) {
        setResults([]);
        setError(err instanceof Error ? err.message : 'Search failed');
      } finally {
        setLoading(false);
      }
    },
    [options?.limit, options?.scopeRootIds],
  );

  useEffect(() => {
    void runSearch(debouncedQuery);
  }, [debouncedQuery, runSearch]);

  const grouped = useMemo(() => groupByContainer(results), [results]);

  return {
    debouncedQuery,
    results,
    grouped: grouped as SearchContainerResult[],
    loading,
    error,
    refresh: () => void runSearch(debouncedQuery),
  };
}
